import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { uploadDocumentWithAuth } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";
import { sentryUtils } from "@/shared/utils/sentry";

import {
  FuneralCostForm,
  EMPTY_FUNERAL_COST_FORM,
  validateFuneralCostFields,
  funeralCostToItemFields,
  type FuneralCostFormValues,
  type FuneralCostFieldErrors,
} from "./FuneralCostForm";

export const AddFuneralCost: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    FuneralCostFormValues,
    FuneralCostFieldErrors
  >(EMPTY_FUNERAL_COST_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting || isAddingItem || !estateId) return;

    const errors = validateFuneralCostFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.invoiceFile) {
        sourceDocumentId = await uploadDocumentWithAuth(
          form.invoiceFile,
          estateId,
          "funeral_invoice",
        );
      }

      const fields = funeralCostToItemFields(form);
      await addItem({
        estate_id: estateId,
        category: "Ongoing Estate Expenses",
        classification: "debt",
        item_name: form.funeralHomeName.trim(),
        item_type: fields.item_type,
        balance: fields.balance,
        source_document_id: sourceDocumentId,
      });

      goToScreen("funeral_costs_manage", {
        ...answers,
        funeral_costs_review: "found",
        funeral_costs_manage: "yes",
        add_funeral_cost: "done",
      });
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddFuneralCost submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AddItemLayout
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting || isAddingItem}
      error={error}
      onSkip={() => {
        goToScreen("ongoing_costs_checklist", {
          ...answers,
          add_funeral_cost: "skipped",
        });
      }}
      skipLabel="I'll add this later"
    >
      <FuneralCostForm form={form} onChange={updateForm} fieldErrors={fieldErrors} />
    </AddItemLayout>
  );
};
